import React from 'react'
import { Col, Form, Row, Table } from 'reactstrap'
import { ErrorMessage, FieldArray, FieldArrayRenderProps, FormikValues, useFormikContext } from 'formik'
import { startCase } from 'lodash'
import { CaseIncident, CasePerformanceShortfall } from '../../../../../WorkflowContainer/workflow-utils'
import TableFormField from '../../../../../../forms/fields/TableFormField'
import { CaseType } from '../../../../../../API'

const incidentColumns = [
  'Date of Hearing',
  'Transgressions found guilty of per hearing notice',
  'Date of Dismissal',
  'Last Day of Remuneration',
  'CCMA Referral Contact Name',
  'CCMA Referral Contact Address',
  'CCMA Referral Contact Email',
]

const shortfallColumns = [
  'Date of Hearing*',
  'Performance shortfalls found guilty of per hearing notice*',
  'Date of Dismissal*',
  'Last Day of Remuneration*',
  'CCMA Referral Contact Name',
  'CCMA Referral Contact Address',
  'CCMA Referral Contact Email',
]

export const InitialHearingExecuteOutcomeSummaryDismissalForm: React.FC<{ caseType: CaseType }> = props => {
  const { values } = useFormikContext<FormikValues>()
  const caseData = values.caseData || {}

  const guiltyShortfalls = React.useMemo(() => {
    const allShortfalls: CasePerformanceShortfall[] = values.performanceShortfalls || [];
    return allShortfalls.filter((item: CasePerformanceShortfall) => {
      const hcRuling = item.initial_hearing_hcApproversFindingOnGuilt;
      const chairGuilty = item.initial_hearing_chairpersonsFindingOnGuilt === 'GUILTY';
      const appealConfirmed = item.appeal_hearing_chairpersonsFinding === 'CONFIRM';
      const appealRejected = item.didEmployeeAppealRuling === 'NO';
      const isHCMissing = hcRuling === undefined || hcRuling === null || hcRuling === '';

      if (caseData.isAppeal) {
        if (appealConfirmed) return true;
        if (hcRuling === 'GUILTY' && appealRejected) return true;
        if (isHCMissing && chairGuilty && appealRejected) return true;
        return false;
      } else {
        if (hcRuling === 'GUILTY') return true;
        if (isHCMissing && chairGuilty) return true;
        return false;
      }
    });
  }, [values.performanceShortfalls, caseData.isAppeal]);

  return (
      <Form>
        <Row>
          <Col>
            <div>
              <h5 className="text-primary text-capitalize font-weight-normal mt-3">
                Compile notice for Summary Dismissal
              </h5>
              <hr style={{ border: '0.06em solid #adb5bd' }} />
              {props.caseType === CaseType.MISCONDUCT && (
                  <Row>
                    <Col>
                      <Table className="table-responsive-lg" bordered>
                        <thead>
                        <tr>
                          {incidentColumns.map(column => (
                              <th key={column}>
                                <span className="text-blue font-weight-500 text-capitalize">{column}</span>
                              </th>
                          ))}
                        </tr>
                        </thead>
                        <tbody>
                        <FieldArray name={'incidents'}>
                          {(arrayHelpers: FieldArrayRenderProps) => (
                              <>
                                {!values.incidents && arrayHelpers.push({})}
                                {values.incidents &&
                                    values.incidents.length &&
                                    values.incidents
                                        .filter((incident: CaseIncident) =>
                                            incident.initial_hearing_hcApproversFindingOnGuilt
                                                ? incident.initial_hearing_hcApproversFindingOnGuilt === 'GUILTY'
                                                : incident.initial_hearing_chairpersonsFindingOnGuilt === 'GUILTY'
                                        )
                                        .map((item: any, index: number) => (
                                            <tr key={index}>
                                              <td style={{ minWidth: '60px', maxWidth: 'auto' }}>
                                                <TableFormField
                                                    type={'date'}
                                                    placeholder={'Date'}
                                                    name={`initial_hearing_hearingDate`}
                                                    className="square-radius border-0 h-25"
                                                />
                                                <span className="text-danger">
                                        <ErrorMessage className="text-danger" name={'initial_hearing_hearingDate'} />
                                      </span>
                                              </td>
                                              <td className="position-relative text-capitalize" style={{ minWidth: '100px' }}>
                                                <span>{startCase(item.transgression.toLowerCase())}</span>
                                              </td>
                                              <td className="position-relative" style={{ minWidth: '60px', maxWidth: 'auto' }}>
                                                <TableFormField
                                                    type={'date'}
                                                    placeholder={'date'}
                                                    name={`dismissalDate`}
                                                    className="square-radius border-0 h-25"
                                                />
                                                <span className="text-danger">
                                        <ErrorMessage className="text-danger" name={'dismissalDate'} />
                                        <div></div>
                                      </span>
                                              </td>
                                              <td className="position-relative" style={{ minWidth: '60px', maxWidth: 'auto' }}>
                                                <TableFormField
                                                    type={'date'}
                                                    placeholder={'date'}
                                                    name={`lastRemunerationDate`}
                                                    className="square-radius border-0 h-25"
                                                />
                                                <span className="text-danger">
                                        <ErrorMessage className="text-danger" name={'lastRemunerationDate'} />
                                        <div></div>
                                      </span>
                                              </td>
                                              <td className="position-relative" style={{ minWidth: '60px' }}>
                                                <TableFormField
                                                    type={'text'}
                                                    placeholder={'Contact Person'}
                                                    className="square-radius border-0 h-25"
                                                    name={`contactPersonInCaseOfCCMA`}
                                                />
                                                <span className="text-danger">
                                        <ErrorMessage className="text-danger" name={'contactPersonInCaseOfCCMA'} />
                                      </span>
                                              </td>
                                              <td style={{ minWidth: '120px' }}>
                                                <TableFormField
                                                    type={'text'}
                                                    placeholder={'Physical Address'}
                                                    name={`physicalAddress`}
                                                    className="square-radius border-0 h-25"
                                                />
                                                <span className="text-danger">
                                        <ErrorMessage className="text-danger" name={'physicalAddress'} />
                                      </span>
                                              </td>
                                              <td className="position-relative" style={{ minWidth: '120px' }}>
                                                <TableFormField
                                                    type={'text'}
                                                    placeholder={'Email Address'}
                                                    name={`emailAddress`}
                                                    className="square-radius border-0 h-25"
                                                />
                                                <span className="text-danger">
                                        <ErrorMessage className="text-danger" name={'emailAddress'} />
                                      </span>
                                              </td>
                                            </tr>
                                        ))}
                              </>
                          )}
                        </FieldArray>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
              )}
              {props.caseType === CaseType.POOR_PERFORMANCE && (
                  <Row>
                    <Col>
                      <Table className="table-responsive-lg" bordered>
                        <thead>
                        <tr>
                          {shortfallColumns.map(column => (
                              <th key={column}>
                                <span className="text-blue font-weight-500 text-capitalize">{column}</span>
                              </th>
                          ))}
                        </tr>
                        </thead>
                        <tbody>
                        <FieldArray name={'performanceShortfalls'}>
                          {(arrayHelpers: FieldArrayRenderProps) => (
                              <>
                                {!values.performanceShortfalls && arrayHelpers.push({})}
                                {guiltyShortfalls &&
                                    guiltyShortfalls.length > 0 &&
                                    guiltyShortfalls.map((item: CasePerformanceShortfall, index: number) => (
                                        <tr key={index}>
                                          <td style={{ minWidth: '60px', maxWidth: 'auto' }}>
                                            <TableFormField
                                                type={'date'}
                                                placeholder={'Date'}
                                                name={`initial_hearing_hearingDate`}
                                                className="square-radius border-0 h-25"
                                            />
                                            <span className="text-danger">
                                      <ErrorMessage className="text-danger" name={'initial_hearing_hearingDate'} />
                                    </span>
                                          </td>
                                          <td className="position-relative text-capitalize" style={{ minWidth: '100px' }}>
                                            <span>{startCase(item.shortfallDescription.toLowerCase())}</span>
                                          </td>
                                          <td className="position-relative" style={{ minWidth: '60px', maxWidth: 'auto' }}>
                                            <TableFormField
                                                type={'date'}
                                                placeholder={'date'}
                                                name={`dismissalDate`}
                                                className="square-radius border-0 h-25"
                                            />
                                            <span className="text-danger">
                                      <ErrorMessage className="text-danger" name={'dismissalDate'} />
                                              {values?.incidents?.dismissalDate && <div>&nbsp; required field </div>}
                                    </span>
                                          </td>
                                          <td className="position-relative" style={{ minWidth: '60px', maxWidth: 'auto' }}>
                                            <TableFormField
                                                type={'date'}
                                                placeholder={'date'}
                                                name={`lastRemunerationDate`}
                                                className="square-radius border-0 h-25"
                                            />
                                            <span className="text-danger">
                                      <ErrorMessage className="text-danger" name={'lastRemunerationDate'} />
                                              {values?.incidents?.lastRemunerationDate && <div>&nbsp; required field </div>}
                                    </span>
                                          </td>
                                          <td className="position-relative" style={{ minWidth: '60px' }}>
                                            <TableFormField
                                                type={'text'}
                                                placeholder={'Contact Person'}
                                                className="square-radius border-0 h-25"
                                                name={`contactPersonInCaseOfCCMA`}
                                            />
                                            <span className="text-danger">
                                      <ErrorMessage className="text-danger" name={'contactPersonInCaseOfCCMA'} />
                                    </span>
                                          </td>
                                          <td style={{ minWidth: '120px' }}>
                                            <TableFormField
                                                type={'text'}
                                                placeholder={'Physical Address'}
                                                name={`physicalAddress`}
                                                className="square-radius border-0 h-25"
                                            />
                                            <span className="text-danger">
                                      <ErrorMessage className="text-danger" name={'physicalAddress'} />
                                    </span>
                                          </td>
                                          <td className="position-relative" style={{ minWidth: '120px' }}>
                                            <TableFormField
                                                type={'text'}
                                                placeholder={'Email Address'}
                                                name={`emailAddress`}
                                                className="square-radius border-0 h-25"
                                            />
                                            <span className="text-danger">
                                      <ErrorMessage className="text-danger" name={'emailAddress'} />
                                    </span>
                                          </td>
                                        </tr>
                                    ))}
                              </>
                          )}
                        </FieldArray>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
              )}
            </div>
          </Col>
        </Row>
      </Form>
  )
}
